import React, { useState } from 'react';
import { useGetIdentity, useNotify, useRecordContext, useUpdate } from 'react-admin';
import { CalendarClock } from 'lucide-react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Switch } from '@mui/material';

import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Funeral } from '../types';
import { isPreplanOnly, preplanLockReason, PreneedMode } from './preneedMode';
import { PreneedStatusPill, PreneedTimeline } from './PreneedTimelineSection';

/** A pure pre-need case: the switch and the Contracts note only apply there. */
export function isOpenPreneed(record: Partial<Funeral> | undefined): boolean {
  return !!record?.has_preneed && !record?.has_atneed;
}

function shortDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * "Pre-plan only" switch (T-172), at the top of the Details tab on pre-need
 * cases. On = the family only wants their information on file: no contract to
 * sign, nothing to pay, so nothing chases them. Off (the default) = prepaid.
 * Locked once the contract is signed, because a signed pre-need is prepaid.
 */
export function PreplanSidebarSection() {
  const record = useRecordContext<Funeral>();
  const [update, { isLoading: isPending }] = useUpdate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [open, setOpen] = useState(false);

  if (!record || !isOpenPreneed(record)) return null;

  const on = isPreplanOnly(record);
  const lockReason = preplanLockReason(record);
  const changed = shortDate(record.preneed_mode_changed_at);
  const by = record.preneed_mode_changed_by;

  const save = () => {
    const next: PreneedMode = on ? 'prepaid' : 'preplan';
    void update(
      'funerals',
      {
        id: record.id,
        data: {
          ...record,
          preneed_mode: next,
          preneed_mode_changed_at: new Date().toISOString(),
          preneed_mode_changed_by: identity?.fullName || null,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify(next === 'preplan' ? 'Marked pre-plan only' : 'Pre-plan only turned off', {
            type: 'success',
          });
          setOpen(false);
        },
        onError: () => notify('Could not update the pre-need plan', { type: 'error' }),
      },
    );
  };

  return (
    <AccordionItem value="preneed">
      <AccordionTrigger icon={<CalendarClock className="w-4 h-4" />}>
        Pre-need
        {!on && <PreneedStatusPill />}
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex justify-between items-center py-1">
          <label htmlFor="preplan-only-switch" className="text-[13px] text-foreground">
            Pre-plan only
          </label>
          <Switch
            id="preplan-only-switch"
            size="small"
            checked={on}
            disabled={!!lockReason || isPending}
            onChange={() => setOpen(true)}
            inputProps={{ 'aria-describedby': 'preplan-only-help' }}
          />
        </div>
        <div id="preplan-only-help" className="text-xs text-muted-foreground pb-1">
          {lockReason
            ? lockReason
            : on
              ? 'Information on file only. No contract or payment expected.'
              : 'Turn on if the family only wants their information on file.'}
          {changed && by ? ` · ${on ? 'Set' : 'Changed'} by ${by}, ${changed}` : ''}
        </div>
        {/* T-162: the TruStage steps. Pre-plan only skips them. */}
        {!on && <PreneedTimeline />}
      </AccordionContent>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{on ? 'Turn off pre-plan only?' : 'Mark as pre-plan only?'}</DialogTitle>
        <DialogContent className="text-sm">
          {on
            ? 'Contract signing and payment follow-ups start again for this case.'
            : 'The family keeps their information on file without signing or paying. Nobody will follow up on the contract or payment.'}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={isPending}>
            {on ? 'Turn off' : 'Mark pre-plan only'}
          </Button>
        </DialogActions>
      </Dialog>
    </AccordionItem>
  );
}

/** Read-only reminder on the Contracts tab while a case is pre-plan only. */
export function PreplanContractsNote() {
  const record = useRecordContext<Funeral>();
  if (!record || !isOpenPreneed(record) || !isPreplanOnly(record)) return null;
  return (
    <div className="mx-5 mt-4 rounded-md bg-muted px-3 py-2.5 text-[12.5px] text-muted-foreground">
      <div className="text-[13px] font-medium text-foreground">Pre-plan only</div>
      No contract or payment expected. To send one, turn off Pre-plan only on the Details tab.
    </div>
  );
}
